import { useMemo } from 'react';
import { Box, Container } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link } from 'react-router-dom';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import {
  KC_DICTIONARY_BASE,
  KC_GUIDE_BASE,
  KC_HOME_PATH,
  formatReadTime,
  guidePath,
  listGuides,
} from './repository';
import { KC_COLORS } from './theme';
import { KcIcon } from './components/KcIcon';
import { GuidesNavigatorCallout } from './components/GuidesNavigatorCallout';
import { KC_TRUNCATE, truncateAtWord } from './truncateAtWord';
import './knowledgeCenter.css';

function guideCardTitle(guide) {
  return guide.title || guide.slug;
}

function cleanSummary(summary = '') {
  return summary
    .replace(/^(IN TWO MINUTES|IN ONE SENTENCE|In one sentence|Two-minute definition|CORE CONCEPT)\s*/i, '')
    .replace(/\n+/g, ' ')
    .trim();
}

function GuidesBreadcrumb() {
  return (
    <nav aria-label="Breadcrumb" className="mb-3">
      <ol
        className="flex flex-wrap items-center gap-x-2 gap-y-1 kc-type-body font-semibold"
        style={{ color: KC_COLORS.navy700, fontSize: '14px', lineHeight: '20px' }}
      >
        <li>
          <Link to="/" className="no-underline" style={{ color: KC_COLORS.navy700 }}>
            Home
          </Link>
        </li>
        <li style={{ color: KC_COLORS.gray600 }}>&gt;</li>
        <li>
          <Link to={KC_HOME_PATH} className="no-underline" style={{ color: KC_COLORS.navy700 }}>
            Knowledge Center
          </Link>
        </li>
        <li style={{ color: KC_COLORS.gray600 }}>&gt;</li>
        <li style={{ color: KC_COLORS.gray600 }}>Reference Guides</li>
      </ol>
    </nav>
  );
}

function GuideListCard({ guide }) {
  const subtitle = truncateAtWord(guide.guideSubtitle, KC_TRUNCATE.oneLine);
  const summary = truncateAtWord(cleanSummary(guide.summary), KC_TRUNCATE.twoLines);

  return (
    <Link
      to={guidePath(guide.slug)}
      className="kc-guides-list-card group relative flex h-full flex-col overflow-hidden rounded-xl border bg-white no-underline transition-all duration-300 hover:-translate-y-0.5"
      style={{ borderColor: KC_COLORS.border, textAlign: 'left' }}
    >
      <span className="kc-guides-list-card-accent" aria-hidden />

      <div className="flex flex-1 flex-col p-3.5 md:p-4">
        <div className="flex items-center justify-between gap-2">
          <span
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ backgroundColor: KC_COLORS.tealLight, color: KC_COLORS.teal }}
          >
            <KcIcon name={guide.icon} sx={{ fontSize: 18 }} />
          </span>
          <span
            className="inline-flex items-center rounded px-1.5 py-0.5 kc-type-caption font-bold"
            style={{ backgroundColor: KC_COLORS.lightGrey, color: KC_COLORS.navy700 }}
          >
            {String(guide.entryNumber || '').padStart(2, '0')}
          </span>
        </div>

        <h2
          className="m-0 mt-2.5 kc-listing-summary"
          style={{
            color: KC_COLORS.primaryNavy,
            fontSize: '16px',
            lineHeight: '22px',
            fontWeight: 700,
          }}
        >
          {guideCardTitle(guide)}
        </h2>

        {subtitle ? (
          <p
            className="m-0 mt-1 kc-guides-list-card-subtitle kc-listing-summary"
            style={{
              color: KC_COLORS.teal,
              fontSize: '11px',
              lineHeight: '15px',
              fontWeight: 600,
            }}
          >
            {subtitle}
          </p>
        ) : null}

        {summary ? (
          <p
            className="m-0 mt-2 flex-1 kc-guides-list-card-summary kc-listing-summary"
            style={{
              color: KC_COLORS.gray600,
              fontSize: '13px',
              lineHeight: '18px',
            }}
          >
            {summary}
          </p>
        ) : (
          <div className="flex-1" />
        )}

        <div
          className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t pt-2"
          style={{ borderColor: KC_COLORS.border }}
        >
          <span
            className="inline-flex items-center gap-1 kc-type-caption font-medium"
            style={{ color: KC_COLORS.navy700 }}
          >
            <AccessTimeOutlinedIcon sx={{ fontSize: 13, color: KC_COLORS.teal }} />
            {formatReadTime(guide.readTimeMinutes)}
          </span>
          <span
            className="ml-auto inline-flex items-center gap-0.5 font-semibold transition-all group-hover:gap-1.5"
            style={{ color: KC_COLORS.teal, fontSize: '12px' }}
          >
            Read
            <ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function KnowledgeCenterGuides() {
  const guides = useMemo(
    () => [...listGuides()].sort((a, b) => (a.entryNumber || 0) - (b.entryNumber || 0)),
    []
  );

  const avgReadMinutes = useMemo(() => {
    if (!guides.length) return 0;
    const total = guides.reduce((sum, guide) => sum + (guide.readTimeMinutes || 0), 0);
    return Math.round(total / guides.length);
  }, [guides]);

  return (
    <>
      <PageSeoHelmet
        pageTitle="Insurance AI Dictionary & Reference Guides | Charlee.ai"
        metaDescription="Browse executive reference guides covering AI concepts for Property & Casualty insurance, from fundamentals through governance."
        canonicalPath={KC_GUIDE_BASE}
      />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="knowledge-center-guides-page">
          <Box
            className="w-full pb-10 md:pb-12"
            style={{
              background:
                'linear-gradient(180deg, #F4F8FA 0%, #FFFFFF 38%, #FFFFFF 100%)',
            }}
          >
            <Container maxWidth="lg">
              <div className="pt-24 md:pt-28 text-left">
                <GuidesBreadcrumb />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-end">
                  <div className="lg:col-span-8 min-w-0">
                    <p
                      className="m-0 kc-type-caption font-bold uppercase tracking-[0.08em]"
                      style={{ color: KC_COLORS.teal }}
                    >
                      Insurance AI Dictionary
                    </p>
                    <h1
                      className="kc-type-h1 m-0 mt-1.5 break-words"
                      style={{ color: KC_COLORS.primaryNavy }}
                    >
                      Reference Guides
                    </h1>
                    <p
                      className="m-0 mt-2 max-w-2xl"
                      style={{
                        color: KC_COLORS.gray600,
                        fontSize: '15px',
                        lineHeight: '22px',
                      }}
                    >
                      Executive reference publications on AI for Property & Casualty
                      insurance—from foundations through governance.
                    </p>
                  </div>

                  <div className="lg:col-span-4 min-w-0">
                    <div
                      className="rounded-xl border bg-white/90 px-3 py-3 sm:px-3.5 shadow-[0_6px_18px_rgba(10,29,58,0.05)]"
                      style={{ borderColor: KC_COLORS.border }}
                    >
                      <div className="grid grid-cols-3 items-center gap-2 sm:gap-4">
                        <div className="min-w-0">
                          <p
                            className="m-0 kc-type-caption font-semibold uppercase tracking-[0.06em]"
                            style={{ color: KC_COLORS.gray600 }}
                          >
                            Guides
                          </p>
                          <p
                            className="m-0 mt-0.5 font-bold text-[20px] leading-6 sm:text-[22px] sm:leading-[26px]"
                            style={{ color: KC_COLORS.primaryNavy }}
                          >
                            {guides.length}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p
                            className="m-0 kc-type-caption font-semibold uppercase tracking-[0.06em]"
                            style={{ color: KC_COLORS.gray600 }}
                          >
                            Avg. read
                          </p>
                          <p
                            className="m-0 mt-0.5 font-bold text-[20px] leading-6 sm:text-[22px] sm:leading-[26px]"
                            style={{ color: KC_COLORS.primaryNavy }}
                          >
                            {avgReadMinutes}m
                          </p>
                        </div>
                        <Link
                          to={KC_DICTIONARY_BASE}
                          className="inline-flex items-center justify-end gap-1 font-semibold no-underline whitespace-nowrap"
                          style={{ color: KC_COLORS.teal, fontSize: '13px' }}
                        >
                          A–Z
                          <ArrowForwardRoundedIcon sx={{ fontSize: 14 }} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <section className="mt-7 md:mt-8 text-left">
                <GuidesNavigatorCallout />
                <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                  <h2
                    className="m-0"
                    style={{
                      color: KC_COLORS.primaryNavy,
                      fontSize: '18px',
                      lineHeight: '24px',
                      fontWeight: 700,
                    }}
                  >
                    All reference guides
                  </h2>
                  <p className="m-0 kc-type-caption font-semibold" style={{ color: KC_COLORS.gray600 }}>
                    {guides.length} publications
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {guides.map((guide) => (
                    <GuideListCard key={guide.slug} guide={guide} />
                  ))}
                </div>
              </section>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterGuidesPage() {
  return <KnowledgeCenterGuides />;
}
